
import React, { useState, useMemo } from "react";
import {
  Box, Typography, FormControl, RadioGroup,
  Paper, Button, Chip, Divider, Collapse, Stack
} from "@mui/material";
import {
  LocationOnOutlined,
  StarOutlined,
  ExpandMoreOutlined,
  ExpandLessOutlined,
  CheckCircleOutlined,
  LocalShippingOutlined,
  InventoryOutlined,
  VerifiedOutlined
} from "@mui/icons-material";

// Color constants
const T = {
  navy: "#0a1628",
  blue: "#1a5aff",
  blueSoft: "#e8efff",
  success: "#00c896",
  surface: "#f7f8fb",
  border: "#e4e8f0",
  muted: "#8898b0",
  white: "#ffffff",
  warning: "#ff9800",
  warningLight: "#fff3e0",
};

// Helper function to get final price
const getFinalPrice = (vendor) => {
  if (vendor?.finalPrice) return vendor.finalPrice;
  if (vendor?.salePrice) return vendor.salePrice;
  if (vendor?.variants?.[0]?.variantSalePrice) return vendor.variants[0].variantSalePrice;
  return 0;
};

// Individual Seller Card Component
const SellerCard = ({ vendor, isSelected, onSelect, showFullDetails = false }) => {
  const price = getFinalPrice(vendor);
  const hasVariants = vendor.variants && vendor.variants.length > 0;
  const lowestVariantPrice = hasVariants
    ? Math.min(...vendor.variants.map(v => parseFloat(v.variantSalePrice || 0)))
    : price;
  const savings = vendor.mrp && price ? Math.round(((vendor.mrp - price) / vendor.mrp) * 100) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `2px solid ${isSelected ? T.blue : T.border}`,
        bgcolor: isSelected ? T.blueSoft : T.white,
        cursor: 'pointer',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': isSelected ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: T.gradBlue || T.blue,
        } : {},
        '&:hover': {
          borderColor: T.blue,
          bgcolor: T.blueSoft,
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(26,90,255,0.12)'
        }
      }}
      onClick={() => onSelect(vendor)}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        {/* Left Section */}
        <Box sx={{ flex: 1 }}>
          {/* Vendor Name and Rating */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, color: T.navy, fontSize: 15 }}>
              {vendor.vendorName}
            </Typography>
            {vendor.isAvailableNow && (
              <Chip
                icon={<VerifiedOutlined sx={{ fontSize: 12 }} />}
                label="Verified Seller"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 9,
                  bgcolor: '#4caf5020',
                  color: '#2e7d32',
                  fontWeight: 600,
                  '& .MuiChip-icon': { fontSize: 12, color: '#4caf50' }
                }}
              />
            )}
            {vendor.averageRating > 0 && (
              <Chip
                icon={<StarOutlined sx={{ fontSize: 12 }} />}
                label={`${vendor.averageRating} ★`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  bgcolor: '#ffc10720',
                  fontWeight: 600,
                  '& .MuiChip-icon': { fontSize: 12, color: '#ffc107', marginLeft: 0.5 }
                }}
              />
            )}
          </Box>

          {/* Location */}
          <Box sx={{ display: 'flex',  gap: 0.75, mb: 1.5 }}>
            <LocationOnOutlined sx={{ fontSize: 13, color: T.muted }} />
            <Typography sx={{ fontSize: 11.5, color: T.muted }}>
              {vendor.address?.split(',')[0] || 'Address not available'}
              {vendor.postalCode && ` • ${vendor.postalCode}`}
            </Typography>
          </Box>

          {/* Full Details (Collapsible) */}
          <Collapse in={showFullDetails} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px dashed ${T.border}` }}>
              {/* <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LocalShippingOutlined sx={{ fontSize: 14, color: T.muted }} />
                  <Typography sx={{ fontSize: 11, color: T.muted }}>
                    Ships from {vendor.address?.split(',')[0]}
                  </Typography>
                </Box>
              </Box> */}
              {hasVariants && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {vendor.variants.map(variant => (
                      <Chip
                        key={variant.uuid}
                        label={`${variant.variantName} - ₹${variant.variantSalePrice}`}
                        size="small"
                        sx={{
                          fontSize: 10,
                          height: 28,
                          bgcolor: T.surface,
                          '&:hover': { bgcolor: T.blueSoft }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Right Section - Price */}
        <Box sx={{ textAlign: 'right', minWidth: 100 }}>
          {/* Stock Status */}
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip
              icon={vendor.isAvailableNow ? <CheckCircleOutlined sx={{ fontSize: 12 }} /> : <InventoryOutlined sx={{ fontSize: 12 }} />}
              label={vendor.isAvailableNow ? `In Stock (${vendor.quantityAvailable})` : 'Out of Stock'}
              size="small"
              sx={{
                height: 24,
                fontSize: 10.5,
                fontWeight: 600,
                bgcolor: vendor.isAvailableNow ? '#00c89620' : '#f4433620',
                color: vendor.isAvailableNow ? '#00865a' : '#d32f2f',
              }}
            />
          </Stack>
          {hasVariants && !showFullDetails && (
            <Typography sx={{ fontSize: 10, color: T.blue, mt: 0.5, fontWeight: 500 }}>
              + {vendor.variants.length} variants
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// Main Seller Selection Component
const SellerSection = ({ vendors, selectedVendor, onVendorSelect, requiredPostalCode }) => {
  const [showAll, setShowAll] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  // Sort vendors to put selected vendor at the top
  const sortedVendors = useMemo(() => {
    if (!selectedVendor) return vendors;
    
    // Create a new array with selected vendor first, then all others in original order
    const otherVendors = vendors.filter(v => v.vendorId !== selectedVendor.vendorId);
    return [selectedVendor, ...otherVendors];
  }, [vendors, selectedVendor]);

  const visibleVendors = useMemo(() => {
    if (showAll) return sortedVendors;
    return sortedVendors.slice(0, 1);
  }, [sortedVendors, showAll]);

  const hasMoreVendors = sortedVendors.length > 1;

  const handleCardExpand = (vendorId) => {
    setExpandedCard(expandedCard === vendorId ? null : vendorId);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{
          fontSize: 12,
          fontWeight: 800,
          color: T.navy,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}>
        Available Sellers ({sortedVendors.length})
        </Typography>
      </Box>

      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <RadioGroup
          value={selectedVendor?.vendorId || ''}
          onChange={(e) => {
            const vendor = vendors.find(v => v.vendorId === e.target.value);
            onVendorSelect(vendor);
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {visibleVendors.map((vendor) => (
              <Box key={vendor.vendorId}>
                <input
                  type="radio"
                  name="vendor"
                  value={vendor.vendorId}
                  checked={selectedVendor?.vendorId === vendor.vendorId}
                  onChange={() => onVendorSelect(vendor)}
                  style={{ display: 'none' }}
                  id={`vendor-${vendor.vendorId}`}
                />
                <label htmlFor={`vendor-${vendor.vendorId}`} style={{ display: 'block', cursor: 'pointer' }}>
                  <SellerCard
                    vendor={vendor}
                    isSelected={selectedVendor?.vendorId === vendor.vendorId}
                    onSelect={onVendorSelect}
                    showFullDetails={expandedCard === vendor.vendorId}
                  />
                </label>
                {/* Expand/Collapse Details Button */}
                {vendor.variants?.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => handleCardExpand(vendor.vendorId)}
                    sx={{
                      mt: 0.5,
                      ml: 2,
                      color: T.blue,
                      fontSize: 11,
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    {expandedCard === vendor.vendorId ? 'Show less' : `View ${vendor.variants.length} variants`}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        </RadioGroup>
      </FormControl>

      {/* View More / View Less Button */}
      {hasMoreVendors && (
        <Button
          onClick={() => setShowAll(!showAll)}
          variant="outlined"
          endIcon={showAll ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
          sx={{
            mt: 2,
            width: '100%',
            py: 1,
            borderRadius: 2,
            borderColor: T.border,
            color: T.blue,
            fontWeight: 600,
            fontSize: 12,
            textTransform: 'none',
            '&:hover': {
              borderColor: T.blue,
              bgcolor: T.blueSoft
            }
          }}
        >
          {showAll ? 'Show Less Sellers' : `Show ${sortedVendors.length - 1} More Sellers`}
        </Button>
      )}
    </Box>
  );
};

export default SellerSection;